import type { EndpointDefinition } from '../contracts';
import {
  AdminDealPathParamsSchema,
  ListDeletedDealsQuerySchema,
  ListDeletedDealsResponseSchema,
} from './admin-deals.schemas';
import {
  AdminOrganizationRecordsPathParamsSchema,
  ListOrganizationRecordsResponseSchema,
  MoveOrganizationRecordsBodySchema,
  OrganizationRecordMovePlanSchema,
  PreviewOrganizationRecordMoveBodySchema,
} from './admin-organization-records.schemas';
import { DealResponseSchema } from './deals.schemas';

/**
 * Admin deal contract group (IMM-851.1). The cross-tenant operator surface,
 * mounted as `contracts.adminDeals` at `/v1/admin/deals`, plus the Standort record
 * move under `/v1/admin/organizations/:id/records` (IMM-1104). Every leaf is
 * admin-tier: `requireAdmin` at the presenter and `backoffice:deal:*` at the
 * route-builder (default-deny). Not part of the published API surface.
 */
export const adminDealContracts = {
  listDeleted: {
    method: 'GET',
    path: '/v1/admin/deals/deleted',
    query: ListDeletedDealsQuerySchema,
    response: { 200: ListDeletedDealsResponseSchema },
    permission: 'backoffice:deal:deal-read',
    description:
      "Every soft-deleted deal across all workspaces, newest deletion first, with the workspace name, the property Objekt-Nr. and whether the property has been deleted too, and who deleted it (`deletedBy`: the actor type and id, with the member's or operator's display name; `null` when no deleter was recorded, `name: null` for the system or a deleter that no longer resolves). Optionally narrowed to one workspace with `?workspaceId=`.",
  },
  restore: {
    method: 'POST',
    path: '/v1/admin/deals/:id/restore',
    pathParams: AdminDealPathParamsSchema,
    response: { 200: DealResponseSchema },
    permission: 'backoffice:deal:deal-restore',
    description:
      "Restore a soft-deleted deal exactly as it was: clears `deleted_at` and the recorded deleter and writes nothing else. 404 when no deal matches the id; 422 `deal.deal.cannot-restore-not-deleted` when the deal is not deleted; 422 `deal.deal.cannot-restore-active-deal-exists` (with `details.blockingDealId` and `details.blockingDealPropertyReference` — the blocking deal's property Objekt-Nr., both nullable) when another DRAFT / ACTIVE / DEFERRED deal already holds the same property and type. A deleted deal that is still open (Entwurf / Aktiv / Zurückgestellt) is refused 422 `deal.deal.cannot-restore-property-archived` when its Objekt is archived; a deleted Verloren deal restores regardless.",
  },
  listOrganizationRecords: {
    method: 'GET',
    path: '/v1/admin/organizations/:id/records',
    pathParams: AdminOrganizationRecordsPathParamsSchema,
    response: { 200: ListOrganizationRecordsResponseSchema },
    permission: 'backoffice:deal:organization-record-read',
    description:
      'Every live deal, Objekt, Kontakt and Lead of one Standort, plus the ACTIVE Standorte of the same workspace the records may move to. 404 `deal.record-move.source-organization-not-found` when no Standort has the id.',
  },
  previewOrganizationRecordMove: {
    method: 'POST',
    path: '/v1/admin/organizations/:id/records/move-preview',
    pathParams: AdminOrganizationRecordsPathParamsSchema,
    body: PreviewOrganizationRecordMoveBodySchema,
    response: { 200: OrganizationRecordMovePlanSchema },
    permission: 'backoffice:deal:organization-record-read',
    description:
      'Plan a move of selected records (1–200) to another Standort without writing anything. The selection is expanded one relation downward — a deal brings its Objekt and Kontakte, a Lead its Kontakt and Objekt, an Objekt its Kontakte — and records that stay linked to records left behind are listed in `staysLinkedTo`. `responsibility` lists, per moving deal and Lead, the responsible members the move keeps, removes and assigns. 404 `deal.record-move.source-organization-not-found`; 422 `deal.record-move.target-not-in-workspace`, `deal.record-move.target-is-source`, `deal.record-move.target-inactive`, or `deal.record-move.records-not-in-source` (with `details.records`) when a selected record is not a live record of this Standort.',
  },
  moveOrganizationRecords: {
    method: 'POST',
    path: '/v1/admin/organizations/:id/records/move',
    pathParams: AdminOrganizationRecordsPathParamsSchema,
    body: MoveOrganizationRecordsBodySchema,
    response: { 200: OrganizationRecordMovePlanSchema },
    permission: 'backoffice:deal:organization-record-move',
    description:
      'Move the selected records and what they bring to another Standort, in one transaction, and answer the plan that was executed. Each moving deal and Lead keeps the responsible members who see the target Standort and loses the others; when none stays, the Standortleitungen of the target (or the workspace Owner) become responsible. `expected` is the `moving` id set and `expectedResponsibility` the `responsibility` (by member id) of the preview the operator confirmed; 409 `deal.record-move.outdated-preview` when the plan computed now differs, or when a moving record left the source Standort while the move ran (nothing is written then). Otherwise the same refusals as the preview.',
  },
} as const satisfies Record<string, EndpointDefinition>;
